// TODO: Overhaul — delete this file once the consumer `/v1/investments/entities/{entityId}` endpoint is reachable
// (#470). Interim, it serves that overhaul path from the live legacy endpoint of the same path (the same
// `EntityInvestmentsDto`), so the Investments detail list reads real investments ahead of the endpoint.
import { Client } from 'openapi-fetch';

import type { AccountsOf } from '@views/investor/queries/investment-accounts/investment-accounts.mock';
import { ok } from '@views/investor/queries/overhaul-mock.util';

import {
  SquirrelMoneyCoreContractsInvestmentAccountDto as InvestmentAccountDto,
  SquirrelMoneyCoreEnumerationsInvestmentAccountTypes as InvestmentAccountTypes,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';
import { paths as api1Paths } from '@core/schemas/api1-schema';

interface GetInit {
  params?: { path?: { entityId?: number }; query?: { pageIndex?: number; pageSize?: number } };
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` fragment serving the entity's investments. Legacy investments know nothing of
 * accounts, so each is assigned to the Term Investment account the accounts mock derived for its class (one per
 * class held) — keeping the list's account names and the "Showing" filter consistent with the account screens.
 */
export function createInvestmentsMockClient(deps: {
  api1: Client<api1Paths>;
  accountsOf: AccountsOf;
}): Client<accountOverhaulPaths> {
  return {
    async GET(_path: string, init?: GetInit) {
      const entityId = init?.params?.path?.entityId;
      if (entityId === undefined) return ok(null);
      const { data, error, response } = await deps.api1.GET('/v1/investments/entities/{entityId}', {
        params: { path: { entityId }, query: init?.params?.query },
      });
      if (error) return { data: undefined, error, response };

      // The FIRST Term Investment account of each class is the one derived from these holdings, so the investments
      // stay with it — an account created later in the same class starts empty. Enum values are compared
      // numerically: the legacy and overhaul schemas each declare their own enum type.
      const termAccountByClass = new Map<number, InvestmentAccountDto>();
      for (const account of await deps.accountsOf(entityId)) {
        const investmentClass = Number(account.investmentClass);
        if (
          account.investmentAccountType === InvestmentAccountTypes.TermInvestment &&
          account.investmentClass != null &&
          !termAccountByClass.has(investmentClass)
        ) {
          termAccountByClass.set(investmentClass, account);
        }
      }
      const items = (data?.investments?.items ?? []).map(investment => {
        const account = termAccountByClass.get(Number(investment.investmentClass));
        return {
          ...investment,
          investmentAccountId: account?.investmentAccountId,
          investmentAccountName: account?.displayName ?? null,
        };
      });
      return ok({ ...data, investments: { ...data?.investments, items } });
    },
    // Cast: this stub implements only the entity investments GET, not the whole overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;
}
